import { IssueCreationSource } from '@gripp/shared-logic';
import {
  PanelHeader,
  RoutineExecutionProps,
  RoutineForm,
  tokens,
} from '@gripp/shared-ui';
import { StyleSheet, View } from 'react-native';

type AnonymousRoutineProps = Pick<
  RoutineExecutionProps,
  'workspaceAsset' | 'module' | 'routine' | 'state' | 'additionalState'
> & {
  onBack: () => void;
  onCompleted: (origin?: string, assetRoutineId?: string) => Promise<void>;
};

// Caps the content column to match anonymousAsset.tsx's CONTENT_MAX_WIDTH.
const CONTENT_MAX_WIDTH = 800;

/**
 * In-page routine view for the anonymous asset flow.
 *
 * Replaces the legacy RoutineModal (which had a fixed capped height that made the
 * submit button unreachable). Renders the full RoutineForm as a page-level swap-in,
 * using the same PanelHeader the authenticated CC flow uses so the back arrow +
 * title chrome is identical.
 */
export const AnonymousRoutine = ({
  workspaceAsset,
  module,
  routine,
  state,
  additionalState,
  onBack,
  onCompleted,
}: AnonymousRoutineProps) => {
  // Prefer the display label configured on the routine button, fall back to name.
  const title = routine?.config?.routineButtonText ?? routine?.name ?? '';

  return (
    <View style={styles.page}>
      {/* Constrain header + form to the same max-width as the asset/routine column. */}
      <View style={styles.contentColumn}>
        <PanelHeader title={title} showBackButton onBackPress={onBack} />

        {/* RoutineForm owns its own KeyboardAwareScrollView — do not wrap in ScrollView. */}
        <View style={styles.formWrapper}>
          <RoutineForm
            workspaceAsset={workspaceAsset}
            module={module}
            routine={routine}
            state={state}
            additionalState={additionalState}
            topBar={() => <></>}
            createdFrom={IssueCreationSource.Anonymousweb}
            onCompleted={onCompleted}
            onCancel={onBack}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    width: '100%',
    backgroundColor: tokens.colors.neutral[100],
    alignItems: 'center',
  },
  contentColumn: {
    flex: 1,
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    flexDirection: 'column',
  },
  formWrapper: {
    flex: 1,
  },
});
